import React, { useEffect, useMemo, useState } from "react";

import {
  Button, Flex, Form, Input, Modal,
  Select,
} from "antd";
import { toast } from "react-toastify";
import { createMlQcRuleRange, updateMlQcRuleRange } from "../../../../actions/qc";
import {
  buildRegularTestsPayload,
  regularTestsToSelectedTests,
  regularTestsValidationRule,
} from "../utils";
import CategorizedTestSelector from "../../../Common/components/CategorizedTestSelector";
import "./EditCustomerRangesModal.css";
import { getUser } from "../../../../utils/helpers";

const REGULAR_TEST_MATCH_SCOPE = "display_name";

const INITIAL_FORM_VALUES = {
  customer_id: "",
  sample_info: "",
  regular_tests: [],
  specs: {
    type: "range",
    min_value: "",
    max_value: "",
    unit: "",
  },
  scope: "",
  match_scope: REGULAR_TEST_MATCH_SCOPE,
};

export default function EditCustomerRangesModal({
  open,
  onCancel,
  editData,
  refetch,
  customerList,
  info,
}) {
  const isEdit = !!editData;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [disableSampleInfo, setDisableSampleInfo] = useState(false);
  const selectedMatchScope = Form.useWatch("match_scope", form) || REGULAR_TEST_MATCH_SCOPE;
  const scopeOptions = Array.isArray(info?.valid_scopes) ? info.valid_scopes : [];
  const matchScopeOptions = Array.isArray(info?.valid_match_scopes) ? info.valid_match_scopes : [];

  useEffect(() => {
    if (editData) {
      const rangeSpec = editData.specs || editData.range_spec || {};
      const regularTestMatchScope = editData.regular_tests?.find((test) => test?.match_scope)?.match_scope
        ?? REGULAR_TEST_MATCH_SCOPE;
      form.setFieldsValue({
        customer_id: editData.customer_id ?? INITIAL_FORM_VALUES.customer_id,
        sample_info: editData.sample_info ?? INITIAL_FORM_VALUES.sample_info,
        regular_tests: regularTestsToSelectedTests(editData.regular_tests, regularTestMatchScope),
        specs: {
          type: rangeSpec.type ?? INITIAL_FORM_VALUES.specs.type,
          min_value: rangeSpec.min_value ?? INITIAL_FORM_VALUES.specs.min_value,
          max_value: rangeSpec.max_value ?? INITIAL_FORM_VALUES.specs.max_value,
          unit: rangeSpec.unit ?? INITIAL_FORM_VALUES.specs.unit,
        },
        scope: editData.scope ?? INITIAL_FORM_VALUES.scope,
        match_scope: regularTestMatchScope,
      });
    }
  }, [editData, form]);

  const onFinish = async (values) => {
    setLoading(true);
    const matchScope = values.match_scope || REGULAR_TEST_MATCH_SCOPE;
    const payload = {
      customer_id: values.customer_id,
      sample_info: values.sample_info,
      regular_tests: buildRegularTestsPayload(values.regular_tests, matchScope),
      range_spec: {
        type: values.specs?.type || "range",
        min_value: values.specs?.min_value,
        max_value: values.specs?.max_value,
        unit: values.specs?.unit,
      },
      scope: values.scope,
      user_id: getUser().id,
    };

    let response;
    if (!isEdit) {
      // create new customer range
      response = await createMlQcRuleRange([payload]);
    } else {
      // edit existing customer range
      payload._id = editData._id;
      response = await updateMlQcRuleRange(payload);
    }

    const successMessage = response.message || (isEdit ? "Customer range updated" : "Customer range created");
    const errorMessage = response.message || "Something went wrong!";

    if (response.success) {
      toast.success(successMessage);
      onCancel();
      if (typeof refetch === "function") {
        refetch();
      }
    } else {
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const onCancelForm = () => {
    form.resetFields();
    onCancel();
  };

  const requiredFormValidation = (message) => ({
    required: true,
    message,
    transform: (value) => value?.trim?.() ?? value,
  });

  const decimalValidation = (message) => ({
    required: true,
    message,
    pattern: /^-?\d*\.?\d+$/,
    transform: (value) => value?.trim?.() ?? value,
  });

  const setSelectedScope = (v) => {
    if (isEdit) return;

    if (v === "generic") {
      form.setFieldsValue({
        sample_info: "",
      });
      setDisableSampleInfo(true);
      form.validateFields(["sample_info"]);
    } else if (v === "sample") {
      setDisableSampleInfo(false);
    }
  };

  const initialFormValues = useMemo(() => {
    if (matchScopeOptions && matchScopeOptions.length > 0) {
      return INITIAL_FORM_VALUES;
    }
    return { ...INITIAL_FORM_VALUES, match_scope: "" };
  }, [matchScopeOptions]);

  return (
    <Modal
      centered
      title={!isEdit ? "Add Customer Range" : "Edit Customer Range"}
      width={1000}
      open={open}
      onCancel={onCancel}
      rootClassName="EditCustomerRangesModal"
      footer={(
        <Flex className="EditCustomerRangesModal__Footer" align="center" justify="flex-end" gap={8}>
          <Button
            type="default"
            onClick={onCancelForm}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            onClick={form.submit}
            loading={loading}
          >
            Save
          </Button>
        </Flex>
      )}
    >
      <Form
        form={form}
        name="customer_ranges"
        initialValues={initialFormValues}
        onFinish={onFinish}
        autoComplete="off"
        colon={false}
        layout="vertical"
        rootClassName="EditCustomerRangesModal__Form"
        requiredMark={false}
      >
        <Flex align="center" gap={16} wrap="wrap">
          <Form.Item
            label="Customer ID"
            name="customer_id"
            rules={[requiredFormValidation("Customer ID required")]}
            className="EditCustomerRangesModal__RuleInput"
          >
            <Select
              allowClear
              showSearch
              className="EditCustomerRulesModal__Input"
              popupClassName="CustomerRange_Customer__Menu"
              placeholder="Select customer"
              options={customerList}
              value={selectedCustomer}
              onSelect={(v) => setSelectedCustomer(v)}
              onClear={() => setSelectedCustomer("")}
              filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
              listHeight={400}
              disabled={isEdit}
            />
          </Form.Item>

          <Form.Item
            label="Scope"
            name="scope"
            rules={[requiredFormValidation("Scope required")]}
            className="EditCustomerRangesModal__RuleInput"
          >
            <Select
              className="EditCustomerRangesModal__Input"
              placeholder="Select scope"
              options={scopeOptions}
              onSelect={(v) => setSelectedScope(v)}
              listHeight={400}
              disabled={isEdit}
            />
          </Form.Item>

          <Form.Item
            label="Sample Info"
            name="sample_info"
            rules={disableSampleInfo || isEdit ? [] : [requiredFormValidation("Sample Info required")]}
            className="EditCustomerRangesModal__RuleInput"
          >
            <Input
              placeholder="Sample Info"
              className="EditCustomerRangesModal__Input"
              disabled={isEdit || disableSampleInfo}
            />
          </Form.Item>

          <Form.Item
            label="Match Scope"
            name="match_scope"
            rules={[requiredFormValidation("Match Scope required")]}
            className="EditCustomerRangesModal__RuleInput"
          >
            <Select
              className="EditCustomerRangesModal__Input"
              placeholder="Select match scope"
              options={matchScopeOptions}
              listHeight={400}
              disabled={isEdit}
            />
          </Form.Item>
        </Flex>

        <Form.Item
          label="Regular Tests"
          name="regular_tests"
          dependencies={["match_scope"]}
          rules={[regularTestsValidationRule]}
        >
          <CategorizedTestSelector
            height={360}
            disabled={isEdit}
            matchScope={selectedMatchScope}
          />
        </Form.Item>

        <div className="EditCustomerRangesModal__ListInputContainer">
          <Flex justify="space-between" align="center" className="EditCustomerRangesModal__ListInputHeader">
            <strong>Specs</strong>
          </Flex>
          <Flex align="center" gap={16} wrap="wrap">
            <Form.Item
              label="Type"
              name={["specs", "type"]}
              rules={[requiredFormValidation("Type required")]}
              className="EditCustomerRangesModal__RuleInput"
            >
              <Input placeholder="Type" className="EditCustomerRangesModal__Input" disabled />
            </Form.Item>
            <Form.Item
              label="Min Value"
              name={["specs", "min_value"]}
              rules={[decimalValidation("Min value must be a valid decimal number")]}
              className="EditCustomerRangesModal__RuleInput"
            >
              <Input
                placeholder="Min Value"
                className="EditCustomerRangesModal__Input"
                type="number"
                step="any"
              />
            </Form.Item>
            <Form.Item
              label="Max Value"
              name={["specs", "max_value"]}
              rules={[decimalValidation("Max value must be a valid decimal number")]}
              className="EditCustomerRangesModal__RuleInput"
            >
              <Input
                placeholder="Max Value"
                className="EditCustomerRangesModal__Input"
                type="number"
                step="any"
              />
            </Form.Item>
            <Form.Item
              label="Unit"
              name={["specs", "unit"]}
              className="EditCustomerRangesModal__RuleInput"
            >
              <Input placeholder="Unit" className="EditCustomerRangesModal__Input" />
            </Form.Item>
          </Flex>
        </div>
      </Form>
    </Modal>
  );
}
